import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Incident } from '../types';

export interface ChartModalState {
  isOpen: boolean;
  incident: Incident | null;
  modalKey: string | null;
}

export interface ChartModalContextValue {
  modalState: ChartModalState;
  openModal: (incident: Incident) => void;
  closeModal: () => void;
  isModalOpen: (modalKey: string) => boolean;
}

const ChartModalContext = createContext<ChartModalContextValue | undefined>(
  undefined,
);

// Maintains modal state outside DataGrid to prevent closure on grid rerenders
export const ChartModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modalState, setModalState] = useState<ChartModalState>({
    isOpen: false,
    incident: null,
    modalKey: null,
  });

  const openModal = useCallback((incident: Incident) => {
    const modalKey = `${incident.id}-${incident.timestamp}`;
    setModalState({
      isOpen: true,
      incident,
      modalKey,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      incident: null,
      modalKey: null,
    });
  }, []);

  const isModalOpen = useCallback(
    (modalKey: string): boolean => {
      return modalState.isOpen && modalState.modalKey === modalKey;
    },
    [modalState.isOpen, modalState.modalKey],
  );

  const contextValue = useMemo(
    () => ({
      modalState,
      openModal,
      closeModal,
      isModalOpen,
    }),
    [modalState, openModal, closeModal, isModalOpen],
  );

  return (
    <ChartModalContext.Provider value={contextValue}>
      {children}
    </ChartModalContext.Provider>
  );
};

export const useChartModalState = (): ChartModalContextValue => {
  const context = useContext(ChartModalContext);

  if (context === undefined) {
    throw new Error(
      'useChartModalState must be used within a ChartModalProvider',
    );
  }

  return context;
};
